import { useState, useEffect, useMemo, useRef } from 'react';
import { II } from '../../components/common/Icons';
import api from '../../services/api';
import HrModal from './HrModal';
import { formatDateOnly } from '../../utils/helpers';
import T from '../../assets/styles/theme';
import "../../assets/styles/HR/HrTicketsPg.css";

// ── helpers ──
const _mKey = (d) => {
  if (!d) return null;
  try { const dt=new Date(d); if(isNaN(dt)) return null; return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const _mLabel = (k) => {
  if (!k) return "";
  const [y,m]=k.split("-");
  return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-IN",{month:"long",year:"numeric"});
};

function exportTicketsCSV(rows, filename) {
  const headers = ["Ticket ID","Employee","Department","Subject","Priority","Status","Source","PM Note","HR Reply","Created On"];
  const lines = [headers.join(",")];
  rows.forEach(t => {
    lines.push([
      t.ticket_id||t.id, t.name||"", t.department||"",
      t.subject||"", t.priority||"Medium", t.status||"",
      t.forwarded_by_pm?"Via PM":"Direct",
      t.pm_note||"", t.hr_reply||t.final_reply||"",
      t.created_at ? new Date(t.created_at).toLocaleDateString("en-IN") : ""
    ].map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

const STATUS_LIST = ['all','open','in_progress','resolved','closed'];
const STATUS_COLORS = {
  all:         { color:"#4f46e5", bg:"#eef2ff" },
  open:        { color:"#2563eb", bg:"#eff6ff" },
  in_progress: { color:"#d97706", bg:"#fef3c7" },
  resolved:    { color:"#16a34a", bg:"#dcfce7" },
  closed:      { color:"#64748b", bg:"#f1f5f9" },
};

function HrTicketsPg() {
  const [tickets,     setTickets]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [replyModal,  setReplyModal]  = useState(null);
  const [escalateModal, setEscalateModal] = useState(null);
  const [escalateNote,  setEscalateNote]  = useState('');
  const [escalateSending, setEscalateSending] = useState(false);
  const [suc,         setSuc]         = useState('');
  const [filter,      setFilter]      = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [showMDrop,   setShowMDrop]   = useState(false);
  const monthDropRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.getHrTickets().then(d=>{ setTickets(d||[]); setLoading(false); }).catch(()=>setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    if (!showMDrop) return;
    const handleClick = (e) => {
      if (monthDropRef.current && !monthDropRef.current.contains(e.target)) {
        setShowMDrop(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMDrop]);

  const handleReply = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const action = fd.get('action');
    const statusMap = { approved:'resolved', rejected:'closed', resend:'in_progress' };
    try {
      await api.replyHrTicket(replyModal.ticket_id||replyModal.id, fd.get('reply'), statusMap[action], action);
      setTickets(prev => prev.map(t =>
        (t.ticket_id||t.id)===(replyModal.ticket_id||replyModal.id)
          ? {...t, hr_reply:fd.get('reply'), status:statusMap[action]||t.status} : t
      ));
      setReplyModal(null);
      setSuc('✅ Reply sent to employee!');
      setTimeout(()=>setSuc(''),4000);
    } catch(e) { alert('Error: '+e.message); }
  };

  const handleEscalateToHR = async () => {
    if (!escalateNote.trim()) { alert('Please describe why you are escalating.'); return; }
    setEscalateSending(true);
    try {
      await api.hrEscalateTicketToPM(escalateModal.ticket_id||escalateModal.id, escalateNote.trim());
      setEscalateModal(null); setEscalateNote(''); setReplyModal(null);
      setSuc('✅ Ticket escalated to PM. They will send instructions back to you.');
      setTimeout(()=>setSuc(''),5000);
      load();
    } catch(e) { alert('Error: '+e.message); }
    setEscalateSending(false);
  };

  const priColor = (p) => ({High:'#ef4444',Critical:'#dc2626',Medium:'#d97706',Low:'#2563eb'})[p]||'#94a3b8';

  // ── derived ──
  const counts = useMemo(() => {
    const c = {all:tickets.length};
    STATUS_LIST.slice(1).forEach(s => { c[s] = tickets.filter(t=>t.status===s).length; });
    return c;
  },[tickets]);

  const availMonths = useMemo(() => {
    const set = new Set();
    tickets.forEach(t=>{ const k=_mKey(t.created_at); if(k) set.add(k); });
    return ["all",...Array.from(set).sort((a,b)=>b.localeCompare(a))];
  },[tickets]);

  const fromPMCount = useMemo(()=>tickets.filter(t=>t.pm_response_to_handler).length,[tickets]);
  const awaitingPM  = useMemo(()=>tickets.filter(t=>t.handler_escalated_to_pm&&!t.pm_response_to_handler).length,[tickets]);

  const filtered = useMemo(() => {
    let list = tickets;
    if (filter!=='all')       list = list.filter(t=>t.status===filter);
    if (monthFilter!=='all')  list = list.filter(t=>_mKey(t.created_at)===monthFilter);
    return list;
  },[tickets, filter, monthFilter]);

  const fromPM  = filtered.filter(t => t.handler_escalated_to_pm);
  const direct  = filtered.filter(t => !t.handler_escalated_to_pm);
  const allRows = [...fromPM, ...direct];

  const actionLogs = tickets
    .filter(t=>t.replied_at||t.reviewed_at||t.updated_at)
    .sort((a,b)=>new Date(b.replied_at||b.reviewed_at||b.updated_at||0)-new Date(a.replied_at||a.reviewed_at||a.updated_at||0))
    .slice(0,20);

  const csvName = () => {
    const p=["tickets"];
    if(filter!=="all") p.push(filter.replace("_","-"));
    if(monthFilter!=="all") p.push(monthFilter);
    return p.join("_")+".csv";
  };

  return (
    <div>
      <div className="sec">Help Desk — Employee Tickets</div>
      {suc && <div className="hrticketspg-inline-1">✓ {suc}</div>}

      {/* ── Status Count Badges ── */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
        {STATUS_LIST.map(s => {
          const sc = STATUS_COLORS[s];
          const active = filter===s;
          return (
            <button key={s} onClick={()=>setFilter(s)} style={{
              display:"flex",flexDirection:"column",alignItems:"center",
              padding:"10px 16px",borderRadius:12,border:"none",cursor:"pointer",minWidth:80,
              background: active ? sc.color : "#f8fafc",
              boxShadow: active ? `0 4px 14px ${sc.color}44` : "0 1px 4px rgba(0,0,0,.06)",
              transition:"all 0.18s ease"
            }}>
              <span style={{fontSize:22,fontWeight:800,color:active?"#fff":sc.color,lineHeight:1.1}}>
                {counts[s]||0}
              </span>
              <span style={{fontSize:10,fontWeight:600,color:active?"rgba(255,255,255,.85)":"#64748b",
                marginTop:2,textTransform:"capitalize"}}>
                {s==="all"?"All":s.replace("_"," ")}
              </span>
            </button>
          );
        })}
        {awaitingPM>0 && (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",
            padding:"10px 16px",borderRadius:12,background:"#fff7ed",minWidth:80,
            boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
            <span style={{fontSize:22,fontWeight:800,color:"#c2410c",lineHeight:1.1}}>{awaitingPM}</span>
            <span style={{fontSize:10,fontWeight:600,color:"#c2410c",marginTop:2}}>⏳ Awaiting PM</span>
          </div>
        )}
        {fromPMCount>0 && (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",
            padding:"10px 16px",borderRadius:12,background:"#f0fdf4",minWidth:80,
            boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
            <span style={{fontSize:22,fontWeight:800,color:"#16a34a",lineHeight:1.1}}>{fromPMCount}</span>
            <span style={{fontSize:10,fontWeight:600,color:"#16a34a",marginTop:2}}>✅ PM Replied</span>
          </div>
        )}
      </div>

      {/* ── Filter + Download toolbar ── */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14,alignItems:"center"}}>

        {/* Month filter */}
        <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
          style={{padding:"8px 10px",border:"1px solid #e2e8f0",borderRadius:8,
            fontSize:12,color:"#374151",background:"#fff",cursor:"pointer"}}>
          <option value="all">All Months</option>
          {availMonths.filter(m=>m!=="all").map(m=>(
            <option key={m} value={m}>{_mLabel(m)}</option>
          ))}
        </select>

        {/* Reset */}
        {(filter!=="all"||monthFilter!=="all") && (
          <button onClick={()=>{setFilter("all");setMonthFilter("all");}}
            style={{padding:"6px 10px",border:"1px solid #fecaca",borderRadius:8,
              background:"#fef2f2",color:"#ef4444",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            ✕ Reset
          </button>
        )}

        {/* Monthly dropdown download */}
        <div ref={monthDropRef} style={{position:"relative",marginLeft:"auto"}}>
          <button onClick={()=>setShowMDrop(v=>!v)}
            style={{display:"flex",alignItems:"center",gap:5,padding:"8px 12px",
              background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
              fontWeight:600,fontSize:12,color:"#374151",cursor:"pointer"}}>
            📅 Monthly CSV ▾
          </button>
          {showMDrop && (
            <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"#fff",
              border:"1px solid #e2e8f0",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,.14)",
              zIndex:300,minWidth:240,overflow:"hidden"}}>
              <div style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:"#64748b",
                borderBottom:"1px solid #f1f5f9",background:"#f8fafc"}}>SELECT MONTH</div>
              {availMonths.filter(m=>m!=="all").length===0 ? (
                <div style={{padding:"12px",fontSize:12,color:"#94a3b8"}}>No data yet</div>
              ) : availMonths.filter(m=>m!=="all").map(m=>{
                const mRows = tickets.filter(t=>_mKey(t.created_at)===m);
                return (
                  <button key={m}
                    onClick={()=>{ exportTicketsCSV(mRows,`tickets_${m}.csv`); setShowMDrop(false); }}
                    style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      width:"100%",padding:"10px 14px",border:"none",background:"#fff",
                      cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc",textAlign:"left"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <span style={{fontWeight:600,color:"#1e293b"}}>{_mLabel(m)}</span>
                    <span style={{fontSize:11,color:"#94a3b8"}}>{mRows.length} tickets</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Download current view */}
        <button onClick={()=>exportTicketsCSV(allRows, csvName())}
          style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",
            background:"#0f172a",color:"#fff",border:"none",borderRadius:8,
            fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
          ⬇ Download CSV {allRows.length!==tickets.length?`(${allRows.length})`:""}
        </button>

        <div style={{color:T.textMuted,fontSize:12}}>{filtered.length} tickets</div>
      </div>

      {/* ── Monthly banner ── */}
      {monthFilter!=="all" && (
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",borderRadius:10,
          padding:"12px 18px",marginBottom:14,display:"flex",justifyContent:"space-between",
          alignItems:"center",color:"#fff"}}>
          <div>
            <div style={{fontWeight:700,fontSize:14}}>📅 {_mLabel(monthFilter)}</div>
            <div style={{fontSize:11,opacity:.8,marginTop:2}}>{filtered.length} tickets this month</div>
          </div>
          <div style={{display:"flex",gap:14}}>
            {["open","in_progress","resolved","closed"].map(s=>{
              const ct=filtered.filter(t=>t.status===s).length;
              return ct>0?(
                <div key={s} style={{textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800}}>{ct}</div>
                  <div style={{fontSize:9,opacity:.75,textTransform:"capitalize"}}>{s.replace("_"," ")}</div>
                </div>
              ):null;
            })}
          </div>
          <button onClick={()=>exportTicketsCSV(allRows,`tickets_${monthFilter}.csv`)}
            style={{padding:"7px 14px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            ⬇ Download Month CSV
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="cd">
        <div className="cd-b hrticketspg-inline-3">
          {loading ? (
            <div style={{color:T.textMuted,padding:30,textAlign:'center'}}>Loading...</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th><th>Dept</th><th>Subject</th>
                  <th>Priority</th><th>Status</th><th>Source</th><th>Date</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {allRows.length===0 ? (
                  <tr><td colSpan="8" style={{textAlign:'center',padding:20,color:T.textMuted}}>
                    {tickets.length===0?"No tickets":"No tickets match the current filter"}
                  </td></tr>
                ) : allRows.map(t=>(
                  <tr key={t.ticket_id||t.id}
                    style={{background: t.handler_escalated_to_pm && !t.pm_response_to_handler ? '#fff7ed'
                      : t.pm_response_to_handler ? '#f0fdf4' : 'transparent'}}>
                    <td className="hrticketspg-inline-5">{t.name||'—'}</td>
                    <td className="hrticketspg-inline-6">{t.department||'—'}</td>
                    <td>{t.subject}</td>
                    <td>
                      <span style={{color:priColor(t.priority),background:priColor(t.priority)+'18',
                        padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700}}>
                        {t.priority||'Medium'}
                      </span>
                    </td>
                    <td>
                      <span className={'chip '+(t.status==='resolved'?'chip-ok':t.status==='closed'?'chip-dn':t.status==='in_progress'?'chip-wn':'chip-ip')}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      {t.pm_response_to_handler
                        ? <span style={{fontSize:11,color:'#16a34a',fontWeight:700}}>✅ PM Replied</span>
                        : t.handler_escalated_to_pm
                          ? <span style={{fontSize:11,color:'#d97706',fontWeight:600}}>⏳ Awaiting PM</span>
                          : <span style={{fontSize:11,color:'#4f46e5',fontWeight:600}}>📥 Direct</span>
                      }
                    </td>
                    <td style={{color:T.textMuted,fontSize:12,whiteSpace:'nowrap'}}>
                      {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : '—'}
                    </td>
                    <td>
                      <button className="btn btn-s" onClick={()=>setReplyModal(t)}
                        style={t.forwarded_by_pm&&t.status!=='resolved'&&t.status!=='closed'
                          ?{background:'#d97706',borderColor:'#d97706',color:'#fff'}:{}}>
                        {t.forwarded_by_pm&&t.status!=='resolved'&&t.status!=='closed'?'📋 Reply':'Reply'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Action log ── */}
      <div className="cd hrticketspg-inline-8">
        <div className="cd-h">
          <div className="cd-t">Action Log</div>
          <div className="cd-s">{actionLogs.length} entries</div>
        </div>
        <div className="cd-b hrticketspg-inline-9">
          {actionLogs.length===0 ? (
            <div style={{color:T.textMuted,padding:16,fontSize:12}}>No logs yet</div>
          ) : actionLogs.map((t,i)=>(
            <div key={(t.ticket_id||t.id)+"-"+i} className="hrticketspg-inline-10">
              <div className="hrticketspg-inline-11">{t.subject}</div>
              <div style={{color:T.textMuted}} className="hrticketspg-dyn-7">
                Status: {t.status} {t.reviewer_action?`· Action: ${t.reviewer_action}`:''} {t.hr_reply?`· Reply: ${t.hr_reply.slice(0,40)}`:''}
              </div>
              <div className="hrticketspg-inline-12">{formatDateOnly(t.replied_at||t.reviewed_at||t.updated_at)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Reply Modal ── */}
      {replyModal && (
        <HrModal open={!!replyModal} title={`Reply — ${replyModal.subject}`} onClose={()=>setReplyModal(null)}>
          <div className="hrticketspg-inline-13">
            <div style={{color:T.textMuted,fontSize:12}}>
              Employee: <b>{replyModal.name}</b> ({replyModal.category})
            </div>
            <div className="hrticketspg-inline-14">{replyModal.description||'No description'}</div>
          </div>
          {replyModal.pm_note && (
            <div style={{background:'#fef9c3',border:'1px solid #fde68a',borderRadius:8,padding:'10px 14px',marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:'#92400e',marginBottom:4}}>
                📋 PM's Note (internal — employee cannot see this)
              </div>
              <div style={{fontSize:13,color:'#78350f',lineHeight:1.5}}>{replyModal.pm_note}</div>
            </div>
          )}
          {/* PM responded to HR escalation */}
          {replyModal.pm_response_to_handler && (
            <div style={{ background:'#f0fdf4', border:'2px solid #86efac', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#16a34a', marginBottom:6 }}>✅ PM has responded — Action Required</div>
              <div style={{ fontSize:13, color:'#166534', lineHeight:1.6, background:'#fff', borderRadius:6, padding:'8px 12px', border:'1px solid #bbf7d0' }}>
                {replyModal.pm_response_to_handler}
              </div>
              <div style={{ fontSize:11, color:'#16a34a', marginTop:8 }}>Review PM's instructions above and reply to the employee accordingly.</div>
            </div>
          )}
          {/* Waiting for PM */}
          {replyModal.handler_escalated_to_pm && !replyModal.pm_response_to_handler && (
            <div style={{ background:'#fefce8', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#b45309', marginBottom:4 }}>⏳ Awaiting PM Response</div>
              <div style={{ fontSize:12, color:'#78350f' }}>You escalated this ticket to PM. They will send instructions back to you shortly.</div>
              {replyModal.handler_escalation_note && <div style={{ fontSize:11, color:'#92400e', marginTop:6 }}>Your note to PM: "{replyModal.handler_escalation_note}"</div>}
            </div>
          )}
          {replyModal.hr_reply && (
            <div className="hrticketspg-inline-15">
              <b>Previous reply:</b> {replyModal.hr_reply}
            </div>
          )}
          <form onSubmit={handleReply}>
            <div className="fg">
              <label className="fl">Reply to Employee *</label>
              <textarea name="reply" className="fi" rows={4}
                placeholder="Type your reply to the employee… (PM note above is NOT included)" required />
            </div>
            <div className="fg">
              <label className="fl">Action</label>
              <select name="action" className="fi" defaultValue="resend">
                <option value="approved">Approve / Resolve</option>
                <option value="rejected">Reject / Close</option>
                <option value="resend">Resend to Employee</option>
              </select>
            </div>
            <p style={{fontSize:11,color:'#64748b',margin:'6px 0 12px'}}>
              📬 Employee will receive <b>only your reply</b> — not the PM's internal note.
            </p>
            <div className="fa" style={{gap:8}}>
              <button type="button" className="btn btn-o" onClick={()=>setReplyModal(null)}>Cancel</button>
              {!replyModal.handler_escalated_to_pm && (
                <button type="button"
                  onClick={() => { setEscalateModal(replyModal); setReplyModal(null); }}
                  style={{ padding:'9px 16px', background:'#fff7ed', color:'#c2410c',
                    border:'1px solid #fed7aa', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                  🔺 Escalate to PM
                </button>
              )}
              <button type="submit" className="btn btn-p">Send Reply to Employee</button>
            </div>
          </form>
        </HrModal>
      )}
      {/* ── Escalate to PM Modal ── */}
      {escalateModal && (
        <HrModal open title="🔺 Escalate Ticket to PM" onClose={() => { setEscalateModal(null); setEscalateNote(''); }}>
          <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#c2410c', marginBottom:4 }}>TICKET</div>
            <div style={{ fontWeight:600, fontSize:13, color:'#1e293b' }}>
              {escalateModal.subject}
            </div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>Employee: <b>{escalateModal.name||'—'}</b></div>
          </div>
          <div className="fg">
            <label className="fl">Why are you escalating this to PM? *</label>
            <textarea className="fi" rows={4}
              placeholder="Describe the issue and why it needs PM's involvement. This note goes to PM only — employee will NOT see it."
              value={escalateNote} onChange={e => setEscalateNote(e.target.value)} />
          </div>
          <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#64748b', lineHeight:1.8 }}>
            <div style={{ fontWeight:600, color:'#374151', marginBottom:4 }}>What happens next:</div>
            <div>① PM receives your note and reviews the ticket privately</div>
            <div>② PM sends instructions back to HR (you)</div>
            <div>③ Employee gets notified: "Your ticket is under review"</div>
            <div>④ You receive PM's instructions and reply to the employee</div>
            <div>⑤ Employee sees HR's final reply in their helpdesk</div>
          </div>
          <div className="fa">
            <button type="button" className="btn btn-o" onClick={() => { setEscalateModal(null); setEscalateNote(''); }}>Cancel</button>
            <button onClick={handleEscalateToHR} disabled={escalateSending || !escalateNote.trim()}
              style={{ padding:'9px 20px', background:'#c2410c', color:'#fff', border:'none',
                borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer',
                opacity:(!escalateNote.trim()||escalateSending)?0.6:1 }}>
              {escalateSending ? 'Escalating…' : '🔺 Send to PM'}
            </button>
          </div>
        </HrModal>
      )}
    </div>
  );
}

export default HrTicketsPg;
